import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useTimeOnTask } from "@/hooks/useTimeOnTask";
import { Loading, scoreTone, GedPrepDisclaimer } from "@/components/common";
import { ScoreMeter } from "@/pages/GedHub";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Timer, Flag, FlagOff, CheckCircle2, XCircle, Info, ArrowLeft, Loader2,
  ChevronLeft, ChevronRight, Send, PenLine, ListChecks, RotateCcw, MessageCircleQuestion,
} from "lucide-react";
import { toast } from "sonner";

const AUTOSAVE_MS = 15000;

const fmtClock = (secs) => {
  if (secs == null || secs < 0) secs = 0;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

// Server-authoritative countdown. Syncs the offset between the browser clock
// and the server's `server_now` from the initial payload so a wrong local clock
// doesn't give/take away time.
const useDeadline = (deadlineISO, serverNowISO) => {
  const offsetRef = useRef(0);
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!deadlineISO) return;
    if (serverNowISO) {
      offsetRef.current = new Date(serverNowISO).getTime() - Date.now();
    }
    const tick = () => {
      const now = Date.now() + offsetRef.current;
      setLeft(Math.max(0, Math.round((new Date(deadlineISO).getTime() - now) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadlineISO, serverNowISO]);
  return left;
};

const wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

const SUBJECT_COPY = {
  math: { icon: "🧮" }, rla: { icon: "📖" }, science: { icon: "🔬" }, social: { icon: "🏛" },
};

export default function GedAttempt() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  useTimeOnTask("ged", attemptId);

  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState(new Set());
  const [essay, setEssay] = useState("");
  const [i, setI] = useState(0);
  const [tab, setTab] = useState("test");
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty | error
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Snapshot of last successfully saved payload (so we only hit the API when something changed).
  const lastSavedRef = useRef("");
  const dirtyRef = useRef(false);

  useEffect(() => {
    api.get(`/ged/attempts/${attemptId}`)
      .then(({ data: d }) => {
        setData(d);
        setAnswers(d.answers || {});
        setFlags(new Set((d.flags || []).map(Number)));
        setEssay(d.essay || "");
        lastSavedRef.current = JSON.stringify({ a: d.answers || {}, e: d.essay || "", f: (d.flags || []).map(Number).sort() });
      })
      .catch((e) => { toast.error(errMsg(e)); navigate("/ged"); });
  }, [attemptId, navigate]);

  const submitted = data?.status === "submitted";
  const deadline = data?.deadline;
  const serverNow = data?.server_now;
  const left = useDeadline(submitted ? null : deadline, serverNow);

  // Build the save payload once per change.
  const buildPayload = useCallback(() => ({
    answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [String(k), Number(v)])),
    flags: Array.from(flags).sort((a, b) => a - b),
    essay: data?.essay_task ? essay : undefined,
  }), [answers, flags, essay, data?.essay_task]);

  const save = useCallback(async (force = false) => {
    if (!data || submitted) return;
    const payload = buildPayload();
    const sig = JSON.stringify({ a: payload.answers, e: payload.essay || "", f: payload.flags });
    if (!force && sig === lastSavedRef.current) return;
    setSaveState("saving");
    try {
      await api.put(`/ged/attempts/${attemptId}/answers`, payload);
      lastSavedRef.current = sig;
      dirtyRef.current = false;
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
    }
  }, [attemptId, data, submitted, buildPayload]);

  // Mark dirty on every change, auto-save on an interval.
  useEffect(() => {
    if (!data || submitted) return;
    dirtyRef.current = true;
    setSaveState((s) => (s === "saving" ? s : "dirty"));
  }, [answers, flags, essay, data, submitted]);

  useEffect(() => {
    if (!data || submitted) return;
    const id = setInterval(() => { if (dirtyRef.current) save(); }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [data, submitted, save]);

  // Save on tab hide / before unload.
  useEffect(() => {
    if (!data || submitted) return;
    const onVis = () => { if (document.hidden) save(); };
    const onUnload = () => { save(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [data, submitted, save]);

  const doSubmit = useCallback(async (silent = false) => {
    if (!data || submitted || busy) return;
    setBusy(true);
    try {
      const payload = buildPayload();
      const { data: res } = await api.post(`/ged/attempts/${attemptId}/submit`, payload);
      setData(res);
      lastSavedRef.current = JSON.stringify({ a: res.answers || {}, e: res.essay || "", f: (res.flags || []).map(Number).sort() });
      if (!silent) toast.success("Test submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [attemptId, data, submitted, busy, buildPayload]);

  // Auto-submit when the clock hits zero.
  useEffect(() => {
    if (!data || submitted || autoSubmitted) return;
    if (left === 0 && deadline) {
      setAutoSubmitted(true);
      doSubmit(true).then(() => toast.message("Time's up — your test was submitted automatically."));
    }
  }, [left, data, submitted, autoSubmitted, deadline, doSubmit]);

  if (!data) return <Loading />;

  const questions = data.questions || [];
  const answeredCount = Object.keys(answers).length;
  const total = questions.length;
  const q = questions[i];
  const flagged = flags.has(i);
  const low = !submitted && left > 0 && left <= 300; // last 5 minutes

  // ---------------- review mode ----------------
  if (submitted && data.result) {
    const r = data.result;
    const review = data.review || [];
    return (
      <div className="max-w-[1000px]">
        <Button variant="ghost" asChild className="mb-4 -ml-3" data-testid="ged-attempt-back">
          <Link to="/ged"><ArrowLeft className="h-4 w-4 mr-1" />Back to GED hub</Link>
        </Button>
        <GedPrepDisclaimer className="mb-4" />
        <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 fade-up" data-testid="ged-result-card">
          <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">
            {SUBJECT_COPY[data.subject]?.icon} {data.title}
            {r.timed_out && <span className="ml-2 text-[#9B2C2C]">· Submitted when time ran out</span>}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-6">
            <div>
              <div className="text-6xl font-display font-semibold text-[#0F7B7D]" data-testid="ged-result-score">{r.estimated_score}</div>
              <div className="text-xs text-muted-foreground">Unofficial practice-test estimate · not an official GED score</div>
            </div>
            <div className="pb-2">
              <div className={`text-sm font-medium rounded-full px-3 py-1 inline-block bg-[#F5F1E6] text-[#123645]`} data-testid="ged-result-band">
                {r.band?.label}
              </div>
              <div className={`mt-2 text-lg font-semibold ${scoreTone(r.percent)}`} data-testid="ged-result-percent">
                {r.correct} / {r.total} correct · {r.percent}%
              </div>
            </div>
          </div>
          <div className="mt-5"><ScoreMeter score={r.estimated_score} />
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1"><span>100</span><span>145 pass</span><span>200</span></div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground max-w-2xl">{r.note}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild data-testid="ged-result-hub"><Link to="/ged">Back to GED hub</Link></Button>
            {r.estimated_score >= 145 && (
              <Button asChild variant="outline" className="bg-white" data-testid="ged-result-real-ged">
                <Link to="/ged/take-real-ged">Ready for the real GED? →</Link>
              </Button>
            )}
            <Button asChild variant="outline" className="bg-white" data-testid="ged-result-tutor">
              <Link to="/tutor"><MessageCircleQuestion className="h-4 w-4 mr-2" />Review with Sage</Link>
            </Button>
          </div>
        </div>

        {r.essay && (
          <div className="mt-6 bg-white border border-border rounded-[18px] p-6" data-testid="ged-essay-feedback">
            <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">Extended response</div>
            <div className="mt-2 text-xl font-display font-semibold">
              {r.essay.total == null ? "Not scored" : `${r.essay.total} / 6`}
            </div>
            {r.essay.total != null && (
              <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                {[["Arguments & evidence", r.essay.trait1], ["Development & structure", r.essay.trait2], ["Clarity & conventions", r.essay.trait3]].map(([lbl, v]) => (
                  <div key={lbl} className="rounded-xl bg-[#F5F1E6] p-3">
                    <div className="text-xs text-muted-foreground">{lbl}</div>
                    <div className="font-semibold">{v} / 2</div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-sm text-[#123645]">{r.essay.feedback}</p>
          </div>
        )}

        {r.topics && r.topics.length > 0 && (
          <div className="mt-6 bg-white border border-border rounded-[18px] p-6" data-testid="ged-topics-breakdown">
            <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">By topic</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {r.topics.map((t) => {
                const pct = Math.round((t.correct / t.total) * 100);
                return (
                  <div key={t.topic} className="rounded-xl border border-border p-3">
                    <div className="flex justify-between text-sm"><span className="font-medium">{t.topic}</span><span className={scoreTone(pct)}>{t.correct} / {t.total}</span></div>
                    <Progress value={pct} className="h-1.5 mt-2" />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 space-y-4" data-testid="ged-review-list">
          {questions.map((qq, k) => {
            const sel = answers[String(k)];
            const rv = review[k] || {};
            const correct = sel === rv.answer_index;
            return (
              <div key={k} className="bg-white border border-border rounded-[14px] p-5" data-testid={`ged-review-${k}`}>
                <div className="flex gap-2 items-start">
                  {correct ? <CheckCircle2 className="h-5 w-5 text-[#1F9D55] shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-[#D64545] shrink-0 mt-0.5" />}
                  <div>
                    <div className="text-xs text-muted-foreground">{qq.topic} · Question {k + 1}</div>
                    {qq.stimulus && <div className="mt-2 text-sm text-[#123645] italic bg-[#FBFAF4] p-3 rounded-lg">{qq.stimulus}</div>}
                    <div className="mt-2 font-medium">{qq.question}</div>
                  </div>
                </div>
                <div className="mt-3 grid gap-2">
                  {qq.options.map((o, oi) => {
                    const isAns = oi === rv.answer_index;
                    const isSel = oi === sel;
                    const cls = isAns ? "border-[#1F9D55]/40 bg-[#DDF7E7]" : isSel ? "border-[#F26B6B]/40 bg-[#FFE1E1]" : "border-border bg-[#FBFAF4]";
                    return (
                      <div key={oi} className={`border rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${cls}`}>
                        <span className="font-semibold w-5">{String.fromCharCode(65 + oi)}.</span>
                        <span>{o}</span>
                        {isAns && <span className="ml-auto text-xs text-[#14663A]">Correct</span>}
                        {isSel && !isAns && <span className="ml-auto text-xs text-[#9B2C2C]">Your answer</span>}
                        {sel === undefined && isAns && <span className="ml-auto text-xs text-[#14663A]">(you left blank)</span>}
                      </div>
                    );
                  })}
                </div>
                {rv.explanation && (
                  <div className="mt-3 bg-[#F5F1E6] rounded-lg p-3 text-sm text-[#123645] flex gap-2">
                    <Info className="h-4 w-4 shrink-0 mt-0.5 text-[#0F7B7D]" />
                    <div><span className="font-semibold">{correct ? "Nice work — " : "Here's why — "}</span>{rv.explanation}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---------------- test-taking mode ----------------
  const setAnswer = (idx, val) => setAnswers((a) => ({ ...a, [String(idx)]: val }));
  const toggleFlag = (idx) => setFlags((f) => { const n = new Set(f); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  const next = () => setI((v) => Math.min(total - 1, v + 1));
  const prev = () => setI((v) => Math.max(0, v - 1));

  const timerCls = left === 0 ? "text-[#9B2C2C]" : low ? "text-[#9B2C2C] animate-pulse" : "text-[#0F7B7D]";
  const saveLabel = saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved" : saveState === "error" ? "Save failed — retry" : "Saved";

  return (
    <div className="max-w-[1100px]">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" asChild className="-ml-3" data-testid="ged-attempt-exit">
          <Link to="/ged"><ArrowLeft className="h-4 w-4 mr-1" />Save & exit</Link>
        </Button>
        <button
          onClick={() => save(true)}
          className={`text-xs rounded-full px-3 py-1 border ${saveState === "error" ? "border-[#F26B6B] text-[#9B2C2C] bg-[#FFE1E1]" : "border-border text-muted-foreground bg-white"}`}
          data-testid="ged-save-state"
          type="button"
        >
          {saveLabel}
        </button>
      </div>

      <GedPrepDisclaimer className="mt-4" compact />

      <div className="bg-white border border-border rounded-[18px] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="ged-attempt-header">
        <div className="min-w-0">
          <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">GED practice test</div>
          <div className="font-display font-semibold text-xl truncate">{SUBJECT_COPY[data.subject]?.icon} {data.title}</div>
          <div className="text-xs text-muted-foreground mt-1">{answeredCount} of {total} answered{data.essay_task ? ` · essay ${wordCount(essay)} words` : ""}</div>
        </div>
        <div className={`flex items-center gap-2 font-display font-semibold text-3xl tabular-nums ${timerCls}`} data-testid="ged-timer">
          <Timer className="h-6 w-6" />{fmtClock(left)}
        </div>
      </div>

      {data.essay_task ? (
        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList data-testid="ged-attempt-tabs">
            <TabsTrigger value="test" data-testid="ged-tab-test"><ListChecks className="h-4 w-4 mr-2" />Multiple choice</TabsTrigger>
            <TabsTrigger value="essay" data-testid="ged-tab-essay"><PenLine className="h-4 w-4 mr-2" />Extended response</TabsTrigger>
          </TabsList>
          <TabsContent value="test" className="mt-5">
            <MCQBody q={q} i={i} total={total} answers={answers} flags={flags} setAnswer={setAnswer} toggleFlag={toggleFlag} prev={prev} next={next} />
          </TabsContent>
          <TabsContent value="essay" className="mt-5">
            <EssayPane task={data.essay_task} essay={essay} setEssay={setEssay} />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="mt-5">
          <MCQBody q={q} i={i} total={total} answers={answers} flags={flags} setAnswer={setAnswer} toggleFlag={toggleFlag} prev={prev} next={next} />
        </div>
      )}

      <Navigator total={total} i={i} setI={setI} answers={answers} flags={flags} />

      <div className="mt-6 flex justify-end">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="h-11" disabled={busy} data-testid="ged-submit-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Submit test for scoring
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="ged-submit-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Submit your practice test?</AlertDialogTitle>
              <AlertDialogDescription>
                You&apos;ve answered <span className="font-semibold">{answeredCount} of {total}</span> questions{flags.size > 0 ? `, flagged ${flags.size}` : ""}{data.essay_task ? `, essay ${wordCount(essay)} words` : ""}.
                Once submitted you can&apos;t change your answers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="ged-submit-cancel">Keep working</AlertDialogCancel>
              <AlertDialogAction onClick={() => doSubmit(false)} data-testid="ged-submit-confirm-ok">Submit now</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ---------------- small sub-components ----------------
const MCQBody = ({ q, i, total, answers, flags, setAnswer, toggleFlag, prev, next }) => {
  if (!q) return null;
  const sel = answers[String(i)];
  const flagged = flags.has(i);
  return (
    <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 fade-up" key={i} data-testid="ged-question-card">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          Question <span className="font-semibold text-[#071A22]">{i + 1}</span> of {total} · <span>{q.topic}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className={`bg-white ${flagged ? "border-[#F2B705] text-[#7A5A00] bg-[#FFF1C7]" : ""}`}
          onClick={() => toggleFlag(i)}
          data-testid="ged-flag-button"
        >
          {flagged ? <FlagOff className="h-4 w-4 mr-1.5" /> : <Flag className="h-4 w-4 mr-1.5" />}
          {flagged ? "Unflag" : "Flag for review"}
        </Button>
      </div>
      {q.stimulus && (
        <div className="mt-4 bg-[#FBFAF4] border border-border rounded-xl p-4 text-[15px] text-[#123645] whitespace-pre-wrap" data-testid="ged-stimulus">
          {q.stimulus}
        </div>
      )}
      <h2 className="mt-4 text-xl sm:text-2xl font-display font-semibold text-[#071A22]" data-testid="ged-question-text">{q.question}</h2>
      <div className="mt-6 grid gap-3" role="radiogroup" aria-label={`Question ${i + 1}`}>
        {q.options.map((o, oi) => {
          const chosen = sel === oi;
          return (
            <button
              key={oi}
              type="button"
              role="radio"
              aria-checked={chosen}
              onClick={() => setAnswer(i, oi)}
              className={`text-left min-h-[56px] rounded-xl border px-4 py-3 flex items-start gap-3 transition-colors duration-150 ${
                chosen ? "border-[#0F7B7D] bg-[#D7F3F1] ring-2 ring-[#0F7B7D]/20" : "border-border bg-white hover:bg-[#F5F1E6]"
              }`}
              data-testid={`ged-option-${oi}`}
            >
              <span className={`h-7 w-7 shrink-0 rounded-full border flex items-center justify-center text-sm font-semibold ${chosen ? "bg-[#0F7B7D] text-white border-[#0F7B7D]" : "border-border"}`}>{String.fromCharCode(65 + oi)}</span>
              <span className="pt-0.5">{o}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-8 flex justify-between gap-3">
        <Button variant="outline" className="bg-white h-11" disabled={i === 0} onClick={prev} data-testid="ged-prev-button">
          <ChevronLeft className="h-4 w-4 mr-1" />Previous
        </Button>
        <Button className="h-11" onClick={next} disabled={i >= total - 1} data-testid="ged-next-button">
          Next<ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

const Navigator = ({ total, i, setI, answers, flags }) => (
  <div className="mt-5 bg-white border border-border rounded-[14px] p-4" data-testid="ged-navigator">
    <div className="text-xs text-muted-foreground mb-2 flex items-center gap-3 flex-wrap">
      <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-[#D7F3F1] border border-[#0F7B7D]/30" />Answered</span>
      <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-white border border-border" />Blank</span>
      <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-[#FFF1C7] border border-[#F2B705]/40" />Flagged</span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: total }, (_, k) => {
        const ans = answers[String(k)] !== undefined;
        const fl = flags.has(k);
        const active = k === i;
        return (
          <button
            key={k}
            type="button"
            onClick={() => setI(k)}
            aria-label={`Go to question ${k + 1}${ans ? ", answered" : ""}${fl ? ", flagged" : ""}`}
            className={`h-8 w-8 rounded-md text-xs font-medium border transition-colors ${
              active ? "bg-[#0F7B7D] text-white border-[#0F7B7D]"
              : fl ? "bg-[#FFF1C7] border-[#F2B705]/40 text-[#7A5A00]"
              : ans ? "bg-[#D7F3F1] border-[#0F7B7D]/30 text-[#0B5B5B]"
              : "bg-white border-border text-muted-foreground"
            }`}
            data-testid={`ged-nav-${k}`}
          >
            {k + 1}
          </button>
        );
      })}
    </div>
  </div>
);

const EssayPane = ({ task, essay, setEssay }) => {
  const words = wordCount(essay);
  const low = words < 150;
  return (
    <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 space-y-5" data-testid="ged-essay-pane">
      <div>
        <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">Topic</div>
        <div className="font-display font-semibold text-lg mt-1">{task.topic}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {["passage_a", "passage_b"].map((k) => (
          <div key={k} className="rounded-xl bg-[#FBFAF4] border border-border p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{k === "passage_a" ? "Passage A" : "Passage B"}</div>
            <div className="font-semibold mt-1">{task[k].title}</div>
            <p className="mt-2 text-sm text-[#123645] whitespace-pre-wrap">{task[k].text}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-[#F5F1E6] p-4 text-sm text-[#123645]" data-testid="ged-essay-prompt">
        <div className="font-semibold mb-1">Your task</div>
        {task.prompt}
      </div>
      <div>
        <div className="flex justify-between items-center mb-2">
          <label htmlFor="ged-essay-input" className="text-sm font-medium">Your response</label>
          <span className={`text-xs ${low ? "text-muted-foreground" : "text-[#14663A]"}`} data-testid="ged-essay-wordcount">{words} words {low && "(aim for 4–7 paragraphs)"}</span>
        </div>
        <Textarea
          id="ged-essay-input"
          value={essay}
          onChange={(e) => setEssay(e.target.value)}
          placeholder="Write your essay here. The GED gives 45 minutes for this task. Reference evidence from both passages."
          className="min-h-[320px] bg-white"
          data-testid="ged-essay-input"
        />
      </div>
    </div>
  );
};
